import { isEqual } from 'lodash';
import { useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import Typography from '@mui/material/Typography';
import { Form, Formik, FormikValues } from 'formik';

import { Icon } from '../icons';

import { AuthLayoutProps } from './types';

export function AuthLayout<Value extends FormikValues>({
  title,
  children,
  onSubmit,
  logoHidden,
  initialValues,
  lowerTitleSize,
  containerClassName,
  ...formikProps
}: AuthLayoutProps<Value>) {
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const valuesRef = useRef<Value>(initialValues);

  const resetMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const onChangeValues = (values: Value) => {
    const isChanged = !isEqual(values, valuesRef.current);
    valuesRef.current = values;
    if (isChanged) {
      resetMessages();
    }
  };

  const renderedMessage = errorMessage || successMessage;

  return (
    <div className="bg-white-primary md:bg-surface-dark flex h-screen w-screen">
      <div className="flex w-full flex-col overflow-y-auto p-6 md:py-20">
        {!logoHidden && (
          <div className="border-gray-primary mb-6 flex flex-wrap justify-center gap-3 border-b pb-6 md:mb-18 md:border-none md:pb-0 [&_img]:h-14 md:[&_img]:h-16">
            <img
              src="/logo.svg"
              className="w-auto"
              alt="Transforming Thyroid Treatment Together Logo"
            />
            <img alt="CHOiCE Logo" className="w-auto" src="/CHOiCE-logo.png" />
          </div>
        )}
        <div className="flex">
          <div
            className={twMerge(
              'md:bg-white-primary md:shadow-blue-primary/10 flex w-full flex-col md:mx-auto md:max-w-106.5 md:justify-center md:rounded-2xl md:p-6 md:shadow-[0px_12px_24px_0px]',
              containerClassName
            )}
          >
            <Typography
              variant="h1"
              className="mb-6 text-center"
              sx={{
                fontSize: lowerTitleSize
                  ? 'var(--h1-size)'
                  : 'var(--h1-hero-size)',
                lineHeight: lowerTitleSize
                  ? 'var(--h1-line)'
                  : 'var(--h1-hero-line)',
              }}
            >
              {title}
            </Typography>

            {renderedMessage && (
              <div
                className={twMerge(
                  'mb-3 flex items-center gap-2 rounded-lg border-[1.5px] border-solid px-4 py-2.5',
                  !!successMessage &&
                    'border-green-primary bg-green-secondary-primary',
                  !!errorMessage &&
                    'bg-red-secondary-primary border-red-primary'
                )}
              >
                {errorMessage ? (
                  <Icon.Info
                    width={20}
                    height={20}
                    className="[&_path]:fill-red-primary shrink-0 rotate-180"
                  />
                ) : (
                  <Icon.CheckCircle
                    width={20}
                    height={20}
                    className="[&_path]:fill-green-primary shrink-0"
                  />
                )}
                <Typography>{renderedMessage}</Typography>
              </div>
            )}

            <div className="flex w-full items-center justify-center">
              <Formik<Value>
                initialValues={initialValues}
                {...formikProps}
                onSubmit={async (values, helpers) => {
                  resetMessages();
                  const result = onSubmit(values, helpers);
                  if (result instanceof Promise) {
                    const promiseResult = await result;
                    if (promiseResult && typeof promiseResult === 'object') {
                      if ('successMessage' in promiseResult) {
                        setSuccessMessage(promiseResult.successMessage);
                      }
                      if ('errorMessage' in promiseResult) {
                        setErrorMessage(promiseResult.errorMessage);
                      }
                    }
                    return promiseResult;
                  } else {
                    return result;
                  }
                }}
              >
                {(props) => {
                  const render =
                    // @ts-ignore
                    children instanceof Function ? children(props) : children;

                  onChangeValues(props.values);

                  return (
                    <div className="w-full">
                      <Form>{render}</Form>
                    </div>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
